import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import i18next from 'i18next';
import {
    ICreateTaskRequest,
    IFetchTasksRequest,
    ITaskResponse,
    TaskStatus
} from '~/components/Tasks/types/tasks.types';
import { RootStore } from '~/stores/root.store';
import { IEmailAttachment, IEvent } from '~/types/model.types';
import { trackError } from '~/services/logger/error.utils';
import { action, toJS } from 'mobx';
import {
    IProjectSearchResponse,
    IFilteredEventResponse
} from '~/types/server.types';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import type useAnalytics from '~/common/useAnalytics';
import { taskHasMeaningfulContent } from '~/components/Tasks/tasks.utils';

const t = i18next.getFixedT(null, 'tasks');

export class TasksController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }
    @action.bound
    async getTasks({
        params = {},
        isGettingNextPage = false,
        withLoader = true
    }: {
        params?: IFetchTasksRequest;
        isGettingNextPage?: boolean;
        withLoader?: boolean;
    } = {}) {
        const {
            tasksStore: {
                setTaskList,
                setNextPageOffsets,
                setTasksPerPageNumber,
                selectedFilter,
                selectedSort,
                taskList,
                currentPage,
                tasksPerPageNumber,
                workspaceId,
                setIsLoading,
                setHasLoadedTasks
            },
            tasksService,
            toastStore
        } = this.rootStore;

        try {
            if (withLoader) {
                setIsLoading(true);
            }
            if (Object.keys(params).length == 0) {
                params.filter_by = selectedFilter;
                params.sort_by = selectedSort.sortBy;
                params.sort_desc = selectedSort.sortDesc;
                params.page = currentPage;
                params.perPage = tasksPerPageNumber;
            }
            const response = await tasksService.fetchTasks(workspaceId, params);

            if (params.perPage) {
                setTasksPerPageNumber(params.perPage);
            }

            const updatedTaskList = isGettingNextPage
                ? [...taskList, ...response.tasks]
                : response.tasks;

            setTaskList(updatedTaskList);

            this.getTaskCount();

            if (response.offsets) {
                setNextPageOffsets(response.offsets);
            }

            return response.tasks;
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: t('errors.fetching_tasks')
            });

            trackError({ error });
        } finally {
            if (withLoader) {
                setIsLoading(false);
                setHasLoadedTasks(true);
            }
        }
    }

    async createTask(
        taskData: Partial<ICreateTaskRequest>,
        analyticsOrigin?: string
    ) {
        const {
            tasksService,
            tasksStore: {
                setTaskList,
                taskList,
                setTaskIdInEdit,
                setTaskInEditAnalyticsOrigin,
                setTaskForCleanup
            },
            toastStore
        } = this.rootStore;

        try {
            const newTask = await tasksService.createTask({
                ...taskData,
                origin: taskData.origin || 'task_management'
            });
            if (['task_management', 'workspace'].includes(taskData.origin)) {
                setTaskIdInEdit(newTask._id);
                setTaskInEditAnalyticsOrigin(analyticsOrigin);
                // Mark new task for potential cleanup
                setTaskForCleanup(newTask._id);
            }
            setTaskList([newTask, ...taskList]);
            this.getTaskCount();

            return newTask;
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: t('errors.general')
            });
            throw error;
        }
    }

    async getTaskCount() {
        const {
            tasksService,
            tasksStore: { setTaskCount, workspaceId }
        } = this.rootStore;

        const taskCount = await tasksService.fetchTaskCount(workspaceId);
        setTaskCount(taskCount);

        return taskCount;
    }

    async getWorkspacesTaskCount(workspaceIds: string[]) {
        try {
            const { tasksService } = this.rootStore;
            return await tasksService.fetchWorkspacesTaskCount(workspaceIds);
        } catch (error) {
            trackError({ error, context: { workspaceIds } });
        }

        return [];
    }

    async updateTask({
        task,
        discardEmptyDescription,
        shouldThrow = true,
        track,
        analyticsOrigin,
        isOptimistic = false
    }: {
        task: ITaskResponse;
        discardEmptyDescription?: boolean;
        shouldThrow?: boolean;
        track: ReturnType<typeof useAnalytics>['track'];
        analyticsOrigin?: string;
        isOptimistic?: boolean;
    }) {
        const {
            tasksService,
            toastStore,
            tasksStore: {
                setTaskIdInEdit,
                taskIdInEdit,
                workspaceId,
                taskInEditAnalyticsOrigin,
                clearTaskCleanup,
                taskIdForCleanup
            },
            userStore: { currUser }
        } = this.rootStore;
        let taskBeforeUpdate: ITaskResponse;
        try {
            // Check if task now has meaningful content and clear cleanup IMMEDIATELY if this is the task marked for cleanup
            if (taskIdForCleanup === task._id) {
                if (taskHasMeaningfulContent(task, currUser?._id)) {
                    clearTaskCleanup();
                }
            }

            if (discardEmptyDescription && !task.description) {
                await this.deleteTask({
                    taskId: task._id,
                    shouldShowSuccessToast: false
                });
                setTaskIdInEdit(null);
                clearTaskCleanup();
                return;
            }
            if (isOptimistic) {
                taskBeforeUpdate = this.updateTaskList(task);
            }

            const updatedTask = await tasksService.updateTask(task);

            if (!isOptimistic) {
                this.updateTaskList(updatedTask);
            }

            if (taskIdInEdit) {
                track(AnalyticsEvents.TasksAddNewTask, {
                    origin:
                        taskInEditAnalyticsOrigin ||
                        analyticsOrigin ||
                        origin ||
                        'task_management_v2',
                    workspace_id: workspaceId
                });
            }

            setTaskIdInEdit(null);
            this.getTaskCount();
            return updatedTask;
        } catch (error) {
            if (taskBeforeUpdate) {
                this.updateTaskList(taskBeforeUpdate);
            }

            toastStore.add({
                type: ToastType.Error,
                text: t('errors.general')
            });
            trackError({ error });
            if (shouldThrow) {
                throw error;
            }
        }
    }

    updateTaskList(task: ITaskResponse): ITaskResponse {
        const {
            tasksStore: { taskList, setTaskList }
        } = this.rootStore;
        let taskBeforeUpdate: ITaskResponse;
        const newTaskList = taskList.map(currentTask => {
            if (currentTask._id === task._id) {
                taskBeforeUpdate = currentTask;
                return task;
            }
            return currentTask;
        });

        setTaskList(newTaskList);
        return taskBeforeUpdate;
    }

    goToProject(projectId, workspaceId, openInNewTab) {
        window.open(
            `event/${projectId}/workspace/${workspaceId}/activity`,
            openInNewTab ? '_blank' : '_self'
        );
    }

    async skipAutomationTask(task) {
        const {
            automationService,
            tasksStore: { setTaskList, taskList },
            toastStore
        } = this.rootStore;

        try {
            await automationService.executeAutomationStep(
                task.workspace_id,
                task._id,
                { approval_status: 'declined' }
            );

            const newTaskList = taskList.filter(
                currentTask => currentTask._id !== task._id
            );

            setTaskList(newTaskList);
            this.getTaskCount();
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: t('errors.general')
            });

            trackError({ error });
        }
    }

    async approveAutomationTask(
        task: ITaskResponse,
        updatedData?: {
            subject: string;
            body: string;
            general_files: IEmailAttachment[];
            image_files: IEmailAttachment[];
        }
    ) {
        const {
            automationService,
            tasksStore: {
                setTaskList,
                taskList,
                workspaceId,
                selectedSort,
                advancedFilters,
                tasksPerPageNumber
            }
        } = this.rootStore;

        try {
            await automationService.executeAutomationStep(
                task.workspace_id,
                task._id,
                {
                    approval_status: TaskStatus.APPROVED,
                    subject: updatedData?.subject,
                    body: updatedData?.body,
                    general_files: updatedData?.general_files,
                    image_files: updatedData?.image_files
                }
            );

            if (!workspaceId) {
                const newTaskList = taskList.filter(
                    currentTask => currentTask._id !== task._id
                );
                setTaskList(newTaskList);
            } else {
                await this.getTasks({
                    params: {
                        ...toJS(advancedFilters),
                        sort_by: selectedSort.sortBy,
                        sort_desc: selectedSort.sortDesc,
                        perPage: tasksPerPageNumber
                    }
                });
            }

            this.getTaskCount();
        } catch (error) {
            trackError({ error });
            throw error;
        }
    }

    async deleteTask({
        taskId,
        shouldShowSuccessToast = true
    }: {
        taskId: string;
        shouldShowSuccessToast?: boolean;
    }) {
        const {
            tasksService,
            tasksStore: {
                setTaskList,
                taskList,
                taskIdForCleanup,
                clearTaskCleanup
            },
            toastStore
        } = this.rootStore;

        try {
            await tasksService.deleteTask(taskId);
            const newTaskList = taskList.filter(
                currentTask => currentTask._id !== taskId
            );

            setTaskList(newTaskList);
            // Clear cleanup flag if this is the task marked for cleanup
            if (taskIdForCleanup === taskId) {
                clearTaskCleanup();
            }
            this.getTaskCount();
            if (shouldShowSuccessToast) {
                toastStore.add({
                    type: ToastType.Success,
                    text: t('toasts.task_deleted')
                });
            }
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: t('errors.general')
            });
            trackError({ error });
        }
    }

    async searchProjects(query: string): Promise<IProjectSearchResponse[]> {
        try {
            const { projectService } = this.rootStore;
            return await projectService.searchProjectList(query);
        } catch (error) {
            trackError({ error, context: { query } });
        }

        return [];
    }

    async getEmail(taskId: string) {
        const { tasksService } = this.rootStore;

        const response = await tasksService.getEmail(taskId);

        return response;
    }

    async fetchRecentProjects(): Promise<IFilteredEventResponse[]> {
        try {
            const { projectService } = this.rootStore;
            return (await projectService.fetchRecentProjects()).data;
        } catch (error) {
            trackError({ error });
        }

        return [];
    }

    async notifyAssignee(taskId: string) {
        try {
            const { tasksService } = this.rootStore;
            await tasksService.notifyAssignee(taskId);
        } catch (error) {
            trackError({ error, context: { taskId } });
        }
    }

    async getProject(projectId: string): Promise<IEvent | null> {
        try {
            const { projectService } = this.rootStore;
            return await projectService.fetchProject(projectId);
        } catch (error) {
            trackError({ error, context: { projectId } });
        }

        return null;
    }

    async getProjectTasks(workspaceId: string) {
        const { tasksService } = this.rootStore;
        try {
            return await tasksService.fetchWorkspaceTasks(workspaceId);
        } catch (error) {
            trackError({ error, context: { workspaceId } });
        }

        return [];
    }

    async importTasks(fromWorkspaceId: string) {
        const {
            tasksService,
            workspaceService,
            tasksStore: { workspaceId: toWorkspaceId, setTaskGroups }
        } = this.rootStore;

        await tasksService.importTasks(toWorkspaceId, fromWorkspaceId);
        const [, , taskGroups] = await Promise.all([
            this.getTasks({}),
            this.getTaskCount(),
            workspaceService.getTaskGroups(toWorkspaceId)
        ]);
        setTaskGroups(taskGroups);
    }
}
